import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  BookOpen, 
  ChevronDown, 
  ChevronUp, 
  ArrowLeft,
  ArrowRight,
  Sparkles
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { useAdmin } from '@/components/admin/AdminContext';
import { SEO } from '@/components/SEO';

// Collapsible section component
const EducationSection = ({ section, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  // Parse content with markdown-like formatting
  const formatContent = (content) => {
    if (!content) return null;
    return content.split('\n\n').map((paragraph, idx) => {
      // Bold text
      const formattedParagraph = paragraph.replace(
        /\*\*(.*?)\*\*/g,
        '<strong>$1</strong>'
      );
      
      // Check if it's a list item
      if (paragraph.startsWith('- ')) {
        const items = paragraph.split('\n').filter(line => line.startsWith('- '));
        return (
          <ul key={idx} className="list-disc list-outside ml-5 space-y-1 mb-4">
            {items.map((item, i) => (
              <li key={i} dangerouslySetInnerHTML={{ __html: item.replace('- ', '').replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') }} />
            ))}
          </ul>
        );
      }
      
      return (
        <p 
          key={idx} 
          className="mb-4 last:mb-0"
          dangerouslySetInnerHTML={{ __html: formattedParagraph }}
        />
      );
    });
  };

  return (
    <Card className="overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left"
      >
        <CardHeader className="flex flex-row items-center justify-between py-4 hover:bg-muted/50 transition-colors">
          <CardTitle className="text-lg font-semibold pr-4">{section.title}</CardTitle>
          <div className="flex-shrink-0">
            {isOpen ? (
              <ChevronUp className="w-5 h-5 text-muted-foreground" />
            ) : (
              <ChevronDown className="w-5 h-5 text-muted-foreground" />
            )}
          </div>
        </CardHeader>
      </button>
      
      {isOpen && (
        <CardContent className="pt-0 pb-6 animate-fade-in">
          <div className="border-t pt-4">
            <div className="prose prose-sm max-w-none text-foreground leading-relaxed">
              {formatContent(section.content)}
            </div>
          </div>
        </CardContent>
      )}
    </Card>
  );
};

export default function EducationPage() {
  const { educationSections } = useAdmin();
  
  // Filter to only published content
  const publishedSections = educationSections
    .filter(s => s.published)
    .sort((a, b) => a.order - b.order);

  return (
    <div className="min-h-screen flex flex-col bg-background">
      <SEO 
        title="Learn About AI | GoCompareAI"
        description="A plain-English guide to AI concepts, data considerations, and how to make informed decisions — without the jargon."
        keywords="AI guide, AI concepts, learn AI, AI for beginners, AI explained"
      />
      <Header />
      
      <main className="flex-1 w-full">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          {/* Back link */}
          <Button
            variant="ghost"
            asChild
            className="mb-6 -ml-2 gap-2"
          >
            <Link to="/">
              <ArrowLeft className="w-4 h-4" />
              Back to comparison tool
            </Link>
          </Button>

          {/* Hero */}
          <div className="text-center mb-10 animate-fade-in">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-6">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            
            <h1 className="text-3xl sm:text-4xl font-semibold text-foreground mb-4">
              Learn About AI
            </h1>
            
            <p className="text-lg text-muted-foreground max-w-xl mx-auto leading-relaxed">
              A plain-English guide to AI concepts, data considerations, and how to make informed decisions — without the jargon.
            </p>
          </div>

          {/* Education sections */}
          <section className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <h2 className="text-xl font-semibold text-foreground">Core concepts</h2>
              <Badge variant="secondary" className="text-xs">
                {publishedSections.length} topics
              </Badge>
            </div>
            
            <div className="space-y-3">
              {publishedSections.map((section, index) => (
                <EducationSection 
                  key={section.id} 
                  section={section}
                  defaultOpen={index === 0}
                />
              ))}
            </div>
          </section>

          {/* Link to Blog */}
          <Card className="bg-muted/30 border-border/50 mb-8">
            <CardContent className="py-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-1">
                    Want more insights?
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Check out our blog for guides, tips, and the latest AI news.
                  </p>
                </div>
                <Button asChild variant="outline" className="gap-2">
                  <Link to="/blog">
                    Read Blog
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* CTA to comparison tool */}
          <div className="text-center">
            <Card className="bg-muted/30 border-border/50">
              <CardContent className="py-8">
                <Sparkles className="w-8 h-8 text-primary mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">
                  Ready to explore your options?
                </h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Our guided questionnaire will help you find AI platforms that match your needs.
                </p>
                <Button asChild>
                  <Link to="/">Start comparison</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
